import type { ReactElement, ReactNode } from 'react';

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface CaseStateCellProps {
  readonly icon: ReactNode;
  /** Headline number for the cell (e.g. "2"). */
  readonly value: string;
  /** Small text appended after the value (e.g. "· 1 excluded"). */
  readonly valueSuffix?: string | undefined;
  /** Description below the value (e.g. "Variants · 1 P/LP · 1 VUS"). */
  readonly label: string;
  /** Visual tone — "warn" rotates icon + value to amber. */
  readonly tone?: 'default' | 'warn';
  /** Tooltip body shown on hover/focus. Omit for non-interactive cells. */
  readonly tooltip?: ReactNode;
}

/**
 * One cell of the case-state strip. Count is the visual hero; a soft
 * icon glyph anchors the row and the small-caps caption explains the
 * count. No tile / chip background — the strip reads as data, not as a
 * row of buttons. Cells with a tooltip become focusable buttons; plain
 * cells render as static spans so empty states stay non-interactive.
 */
export function CaseStateCell({
  icon,
  value,
  valueSuffix,
  label,
  tone = 'default',
  tooltip,
}: CaseStateCellProps): ReactElement {
  const inner = (
    <span className="flex items-center gap-2 px-3 py-1.5 min-w-0 w-full text-left">
      <span
        aria-hidden="true"
        className={cn(
          'shrink-0',
          tone === 'warn' ? 'text-warning' : 'text-muted-foreground/80',
        )}
      >
        {icon}
      </span>
      <span className="min-w-0 flex flex-col leading-tight">
        <span className="flex items-baseline gap-1 min-w-0">
          <span
            className={cn(
              'shrink-0 text-[15px] font-semibold tracking-tight tabular-nums',
              tone === 'warn' ? 'text-warning' : 'text-foreground',
            )}
          >
            {value}
          </span>
          {valueSuffix !== undefined && (
            <span className="text-[10.5px] text-muted-foreground tabular-nums truncate">
              {valueSuffix}
            </span>
          )}
        </span>
        <span
          className={cn(
            'text-[9.5px] font-semibold uppercase tracking-[0.08em] truncate',
            tone === 'warn' ? 'text-warning/90' : 'text-muted-foreground',
          )}
        >
          {label}
        </span>
      </span>
    </span>
  );

  if (tooltip === undefined) {
    return <span className="flex">{inner}</span>;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex outline-none rounded-sm cursor-default transition-colors',
            'hover:bg-accent/30 focus-visible:ring-2 focus-visible:ring-ring',
          )}
        >
          {inner}
        </button>
      </TooltipTrigger>
      <TooltipContent
        side="bottom"
        align="start"
        className="max-w-md p-3"
      >
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
}
